import { useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { t } from '../i18n';

// Semi-transparent tints so the block-boundary / PI-tail highlights read well on
// BOTH the light (#fafaf7-ish card) and dark (#1f1f1f card) grounds — the earlier
// solid pastels (#eaf3f8 / #fff3d0) turned the whole dump into a white box in dark
// mode. The container itself uses var(--card)/var(--border) now.
const TINT_BLOCK = 'rgba(120, 170, 220, 0.18)';
const TINT_PI = 'rgba(250, 173, 20, 0.22)';

const REGIONS: Array<{ id: number; label: string }> = [
  { id: 0, label: 'r0 FIX_HEADER' },
  { id: 1, label: 'r1 FIX_HEADER_BAK' },
  { id: 2, label: 'r2 AREA_HEADER' },
  { id: 3, label: 'r3 AREA_HEADER_BAK' },
  { id: 4, label: 'r4 AUTHENTICATION' },
  { id: 5, label: 'r5 AUTHENTICATION_BAK' },
  { id: 6, label: 'r6 ELEC_MARK (elabel)' },
  { id: 7, label: 'r7 ELEC_MARK_BAK' },
  { id: 8, label: 'r8 SMART' },
  { id: 9, label: 'r9 TECH_PARAM' },
  { id: 10, label: 'r10 TECH_PARAM_BAK' },
  { id: 11, label: 'r11 INNER_PARAM' },
];

interface Props {
  sessionId: string;
  serial: string;
  /** Disk bus from the session summary. PI Guard is NVMe-only; SAS/4Kn tails
   * are data, not a Guard. Absent → we do NOT label any tail as PI. */
  transport?: string | null;
}

export function HexViewer({ sessionId, serial, transport }: Props) {
  const [regionId, setRegionId] = useState(6); // start with elabel — most interesting

  const q = useQuery({
    queryKey: ['region-raw', sessionId, serial, regionId],
    queryFn: () => api.fetchRegionRaw(sessionId, serial, regionId),
  });

  const bytes = useMemo(() => {
    if (!q.data) return null;
    const bin = atob(q.data.base64);
    const u = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) u[i] = bin.charCodeAt(i);
    return u;
  }, [q.data]);

  // Wire geometry. Prefer server-reported values; otherwise infer from the
  // disk's transport. PI Guard is NVMe-only, so a SAS 520/4Kn tail stays DATA.
  // When the transport is unknown, err on "data" (never mislabel a tail as PI).
  const isNvme = (transport ?? '').toLowerCase() === 'nvme';
  const hasPi = q.data?.hasProtectionInfo ?? isNvme;
  // The server reports the real wire block size; fall back to 520 only if absent.
  const blockBytes = q.data?.blockSize ?? 520;
  const piTail = hasPi ? 8 : 0; // T10 PI Guard is the last 8 bytes of the block

  return (
    <div>
      <div className="row" style={{ marginBottom: 10 }}>
        <label className="muted">{t('hexRegionLabel')}:</label>
        <select value={regionId} onChange={(e) => setRegionId(Number(e.target.value))}>
          {REGIONS.map((r) => (
            <option key={r.id} value={r.id}>
              {r.label}
            </option>
          ))}
        </select>
        {q.data && (
          <span className="muted">
            {t('hexBytesUnit', { n: q.data.sizeBytes })} · {q.data.regionName}
          </span>
        )}
      </div>
      {/* Legend: only advertise a PI tail when this disk actually carries one. */}
      <div className="muted" style={{ fontSize: 11, marginBottom: 6 }}>
        <span style={{ background: TINT_BLOCK, padding: '0 4px' }}>{t('hexLegendBlock', { n: blockBytes })}</span>
        {piTail > 0 && (
          <span style={{ background: TINT_PI, padding: '0 4px', marginLeft: 6 }}>{t('hexLegendPi')}</span>
        )}
      </div>
      {q.isLoading && <div className="muted">{t('hexLoading')}</div>}
      {bytes && <HexDump bytes={bytes} blockBytes={blockBytes} piTail={piTail} />}
      {q.data?.parsed && q.data.parsed.kind !== 'none' && (
        <RegionParsed parsed={q.data.parsed} />
      )}
    </div>
  );
}

type ParsedRegion = NonNullable<Awaited<ReturnType<typeof api.fetchRegionRaw>>['parsed']>;

/** Read-only structural decode of the selected region, shown under the hex dump
 * (mirrors the HwReserveEditor per-region parsers). */
function RegionParsed({ parsed }: { parsed: ParsedRegion }) {
  const box: CSSProperties = {
    marginTop: 14,
    border: '1px solid var(--border)',
    borderRadius: 6,
    padding: '10px 12px',
    background: 'var(--card)',
  };
  const kv = (rows: Array<{ label: string; value: string }>) => (
    <table style={{ width: '100%', fontSize: 12 }}>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            <td style={{ padding: '2px 8px 2px 0', color: 'var(--muted)', whiteSpace: 'nowrap', verticalAlign: 'top' }}>
              {r.label}
            </td>
            <td style={{ padding: '2px 0', wordBreak: 'break-all' }}>
              <code>{r.value || '—'}</code>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
  return (
    <div style={box}>
      <div className="muted" style={{ fontSize: 11, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {t('hexParsedTitle')}
      </div>
      {(parsed.kind === 'elabel' ||
        parsed.kind === 'innerParam' ||
        parsed.kind === 'cert' ||
        parsed.kind === 'techParam' ||
        parsed.kind === 'fixHeader' ||
        parsed.kind === 'authMarker') &&
        (parsed.fields.length ? kv(parsed.fields) : <div className="muted" style={{ fontSize: 12 }}>{t('emptyList')}</div>)}
      {parsed.kind === 'regionMap' && (
        <>
          {kv([{ label: 'CRC-32/BZIP2', value: parsed.signature }])}
          <table style={{ width: '100%', fontSize: 12, marginTop: 8 }}>
            <thead>
              <tr style={{ color: 'var(--muted)', textAlign: 'left' }}>
                <th style={{ padding: '2px 8px 2px 0', fontWeight: 500 }}>{t('hexParsedRegion')}</th>
                <th style={{ padding: '2px 8px 2px 0', fontWeight: 500, textAlign: 'right' }}>{t('hexParsedOffset')}</th>
                <th style={{ padding: '2px 0', fontWeight: 500, textAlign: 'right' }}>{t('hexParsedLength')}</th>
              </tr>
            </thead>
            <tbody>
              {parsed.entries.map((e, i) => (
                <tr key={i}>
                  <td style={{ padding: '2px 8px 2px 0' }}>{e.label}</td>
                  <td style={{ padding: '2px 8px 2px 0', textAlign: 'right' }}>
                    <code>0x{e.offset.toString(16).toUpperCase()}</code>
                  </td>
                  <td style={{ padding: '2px 0', textAlign: 'right' }}>
                    <code>{e.length}</code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

function HexDump({ bytes, blockBytes, piTail }: { bytes: Uint8Array; blockBytes: number; piTail: number }) {
  // 16 bytes per row, hex + ASCII. Rows within the PI tail of each wire block
  // (NVMe only) get a distinct colour; the block boundary is tinted otherwise.
  const rows = [];
  const total = bytes.length;
  const userBytes = blockBytes - piTail;
  for (let off = 0; off < total; off += 16) {
    const slice = bytes.subarray(off, Math.min(off + 16, total));
    const hex: string[] = [];
    let ascii = '';
    for (let i = 0; i < 16; i++) {
      if (i < slice.length) {
        const b = slice[i]!;
        hex.push(b.toString(16).padStart(2, '0'));
        ascii += b >= 0x20 && b < 0x7f ? String.fromCharCode(b) : '·';
      } else {
        hex.push('  ');
        ascii += ' ';
      }
    }
    const offsetInBlock = off % blockBytes;
    // Only tint a PI tail when this disk carries T10-PI (NVMe). For SAS/4Kn the
    // trailing bytes are data, so piTail is 0 and nothing is mislabelled.
    const isPi = piTail > 0 && offsetInBlock >= userBytes;
    const isBlockBoundary = offsetInBlock === 0;
    rows.push(
      <div
        key={off}
        style={{
          background: isPi ? TINT_PI : isBlockBoundary ? TINT_BLOCK : undefined,
          fontFamily: 'Consolas, "Cascadia Mono", monospace',
          fontSize: 11,
          lineHeight: '16px',
          whiteSpace: 'pre',
          padding: '0 4px',
        }}
      >
        <span style={{ color: '#999', marginRight: 8 }}>{off.toString(16).padStart(5, '0')}</span>
        <span>{hex.slice(0, 8).join(' ')}  {hex.slice(8, 16).join(' ')}</span>
        <span style={{ marginLeft: 12, color: '#666' }}>|{ascii}|</span>
      </div>,
    );
  }

  return (
    <div
      style={{
        maxHeight: 480,
        overflow: 'auto',
        border: '1px solid var(--border)',
        borderRadius: 4,
        background: 'var(--card)',
        padding: '4px 0',
      }}
    >
      {rows}
    </div>
  );
}
